import React from "react";
import {
  Sparkles,
  Image as ImageIcon,
  Palette,
  Users,
  Check,
} from "lucide-react";
import { toTitleCase } from "../../utils/titleCase";
import { getTeamStyle } from "../../utils/teamColors";

/**
 * Pure display + toggle component. All state lives in Register.jsx —
 * this just renders checkboxes and reports toggles up.
 *
 * NOTE: PlayerAutocomplete no longer reacts to name changes made from here —
 * see the internalValueRef guard in PlayerAutocomplete.jsx. Importing a
 * player will not pop open the "returning players" dropdown for that field.
 */
const ImportPreviousTeamPanel = ({
  importSource, // { team, players } or null
  loading,
  selections, // { teamName: bool, logo: bool, colors: bool }
  onToggleTeamName,
  onToggleLogo,
  onToggleColors,
  importedPlayerIds, // Set of player.id currently imported into the roster
  onTogglePlayer,
}) => {
  if (loading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-transparent dark:border-gray-600 animate-spin" />
        Checking for a previous team...
      </div>
    );
  }

  if (!importSource) {
    return (
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-400">
        No previous team found for this account — fill out the form below as
        normal.
      </div>
    );
  }

  const { team, players } = importSource;
  const importedCount = players?.length
    ? players.filter((p) => importedPlayerIds.has(p.id)).length
    : 0;

  // Reuse the app's existing team-color utility so a returning captain's own
  // colors show through here, instead of a generic green card.
  // NOTE: verify these key names (`headerStyle`, `accentHex`) match the real
  // return shape of getTeamStyle in src/utils/teamColors.js — adjust if not.
  const teamStyle =
    team.primary_color || team.secondary_color
      ? (getTeamStyle(team) ?? {})
      : {};
  const { headerStyle, accentHex } = teamStyle;

  // Generic green card when the team has no colors on file yet; otherwise
  // headerStyle (from getTeamStyle) takes over via inline style.
  const fallbackCardClasses =
    "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800";

  return (
    <div
      className={`mb-8 rounded-xl border p-6 animate-fade-in ${
        headerStyle ? "" : fallbackCardClasses
      }`}
      style={headerStyle || undefined}
    >
      <div className="flex items-start gap-3 mb-1">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/60 dark:bg-black/20"
          style={accentHex ? { color: accentHex } : undefined}
        >
          <Sparkles
            className={accentHex ? "" : "text-green-600 dark:text-green-400"}
            size={16}
          />
        </span>
        <div className="min-w-0">
          <h3 className="font-bold text-lg dark:text-white">
            Welcome back! Import from "{toTitleCase(team.name)}"?
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Check anything you'd like to carry over. Unchecked items start
            blank, same as a new registration.
          </p>
        </div>
      </div>

      <div className="space-y-2 my-4 pl-11">
        <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 -ml-2 hover:bg-white/50 dark:hover:bg-black/10 cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={selections.teamName}
            onChange={onToggleTeamName}
            className="w-4 h-4 accent-orange-600"
          />
          <span className="dark:text-white text-sm">
            Team Name —{" "}
            <span className="font-medium">{toTitleCase(team.name)}</span>
          </span>
        </label>

        {team.logo_url && (
          <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 -ml-2 hover:bg-white/50 dark:hover:bg-black/10 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={selections.logo}
              onChange={onToggleLogo}
              className="w-4 h-4 accent-orange-600"
            />
            <span className="dark:text-white text-sm flex items-center gap-2">
              <ImageIcon
                size={14}
                className="text-gray-400 dark:text-gray-500"
              />
              Team Logo
              <img
                src={team.logo_url}
                alt="Previous team logo"
                className="h-6 w-6 rounded object-cover ring-1 ring-black/5"
              />
            </span>
          </label>
        )}

        {(team.primary_color || team.secondary_color) && (
          <label className="flex items-center gap-2 rounded-lg px-2 py-1.5 -ml-2 hover:bg-white/50 dark:hover:bg-black/10 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={selections.colors}
              onChange={onToggleColors}
              className="w-4 h-4 accent-orange-600"
            />
            <span className="dark:text-white text-sm flex items-center gap-2">
              <Palette size={14} className="text-gray-400 dark:text-gray-500" />
              Team Colors
              <span
                className="inline-block h-4 w-4 rounded-full border border-gray-300 shadow-sm"
                style={{
                  backgroundColor: team.primary_color
                    ? `#${team.primary_color}`
                    : "#ccc",
                }}
              />
              <span
                className="inline-block h-4 w-4 rounded-full border border-gray-300 shadow-sm"
                style={{
                  backgroundColor: team.secondary_color
                    ? `#${team.secondary_color}`
                    : "#ccc",
                }}
              />
            </span>
          </label>
        )}
      </div>

      {players?.length > 0 && (
        <div className="pl-11">
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold dark:text-white text-sm flex items-center gap-1.5">
              <Users size={14} className="text-gray-400 dark:text-gray-500" />
              Players
            </p>
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {importedCount} of {players.length} imported
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {players.map((p) => {
              const imported = importedPlayerIds.has(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
                    imported
                      ? "bg-white/70 dark:bg-black/20"
                      : "hover:bg-white/50 dark:hover:bg-black/10"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={imported}
                    onChange={() => onTogglePlayer(p)}
                    className="w-4 h-4 accent-orange-600"
                  />
                  <span className="dark:text-white text-sm flex items-center gap-1.5">
                    {p.name}{" "}
                    {p.jersey_number != null ? `#${p.jersey_number}` : ""}
                    {imported && (
                      <Check
                        size={13}
                        className="text-green-600 dark:text-green-400"
                      />
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportPreviousTeamPanel;
